import { withEventContext } from "@codat/analytics";
import { CircleQuestionIcon, Typography } from "@codat/orchard-ui";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import { SourceTypeStatus, useSourceTypes } from "contexts/SourceTypesContext";
import useCompleteRedirect from "hooks/useCompleteRedirect";
import useContinueRedirectUrl from "hooks/useContinueRedirectUrl";
import useFilteredQueryString from "hooks/useFilteredQueryString";
import { useSourceTypeResolver } from "hooks/useSourceTypeResolver";
import { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  getSourceTypeLinkUrl,
  sourceTypeHasData,
  usePageViewedEvent,
} from "utils";

import Button from "components/Button";
import ConnectionComplete from "components/ConnectionComplete";
import HelpModal from "components/HelpModal";
import LinkPageRoot from "components/LinkPageRoot";
import SourceTypeSummary from "components/SourceTypeSummary";
import { ConnectionParams } from "config/urls";

import styles from "./CompanyOverview.module.css";

export type CompanyOverviewProps = RouteComponentProps<ConnectionParams>;

const CompanyOverviewContent: React.FC<CompanyOverviewProps> = ({ match }) => {
  const history = useHistory();
  const { state: config } = useConfig();
  const [isHelpModal, setHelpModal] = useState(false);
  const search = useFilteredQueryString();
  const { redirectUrl: continueRedirectUrl } = useContinueRedirectUrl(
    match.params.companyId
  );

  const { sourceTypes, status: sourceTypesStatus } = useSourceTypes();
  const { currentSourceType } = useSourceTypeResolver(
    match.params,
    sourceTypes
  );

  const isSuccess = sourceTypes?.some(sourceTypeHasData);

  const isComplete = useMemo(
    () =>
      sourceTypesStatus === SourceTypeStatus.READY &&
      config.status === ConfigStatusType.READY &&
      !!currentSourceType,
    [config.status, sourceTypesStatus, currentSourceType]
  );

  usePageViewedEvent({
    ready: isComplete,
    clientConfig: config.data,
    sourceTypes,
  });

  const redirect = useCompleteRedirect({
    companyId: match.params.companyId,
    sourceType: currentSourceType?.sourceType || "",
    statusCode: 201,
  });

  const onFinish = useMemo(() => {
    if (continueRedirectUrl) {
      return () => window.location.assign(continueRedirectUrl);
    } else if (config.data.hasCustomRedirect) {
      return redirect;
    } else {
      return () => history.push("/completed");
    }
  }, [continueRedirectUrl, config.data.hasCustomRedirect, history, redirect]);

  const onRestart = () => {
    const url = getSourceTypeLinkUrl(match.params.companyId, currentSourceType);
    history.push({ pathname: url, search });
  };

  const { help, colors } = config.data;
  const showHelp = help?.description || help?.link?.href || help?.link?.text;

  return (
    <LinkPageRoot
      isComplete={isComplete}
      data-testid="connections-overview-page"
      showLinkedAccounts
    >
      <ConnectionComplete
        title={
          isSuccess
            ? "Thank you for sharing your data"
            : "You haven’t shared your data"
        }
      >
        <Typography variant="p" as="p" className={styles.subtitle}>
          {config.data.clientName}{" "}
          {isSuccess
            ? `will now be able to access your financial information`
            : `will not be able to access your financial information, please try again`}
        </Typography>
        <ul className={styles.sourceTypeSummaryWrapper}>
          {sourceTypes?.map((sourceType) => (
            <li key={sourceType.sourceType}>
              <SourceTypeSummary
                sourceType={sourceType.sourceType}
                connectedCount={sourceType.connected}
                disconnectedCount={sourceType.disconnected}
                uploadedFilesCount={sourceType.uploadedFiles}
              />
            </li>
          ))}
        </ul>
        <div className={styles.footer}>
          {isSuccess ? (
            <Button label="Finish" analyticsName="Finish" onClick={onFinish} />
          ) : (
            <>
              {showHelp && (
                <Button
                  style={{ color: colors?.primary }}
                  onClick={() => setHelpModal(true)}
                  data-testid="overview-help"
                  variant="tertiary"
                  analyticsName="Open help"
                  eventProperties={{ connectedSourceTypes: 0 }}
                  className={styles.helpLink}
                >
                  <CircleQuestionIcon className={styles.icon} />
                  <span>Help</span>
                </Button>
              )}
              <Button
                label="Restart"
                analyticsName="Restart"
                onClick={onRestart}
              />
            </>
          )}
        </div>
      </ConnectionComplete>
      <HelpModal
        isOpen={isHelpModal}
        onClose={() => setHelpModal(false)}
        text={help?.description}
        link={help?.link}
      />
    </LinkPageRoot>
  );
};

export const CompanyOverview = withEventContext<
  CompanyOverviewProps & JSX.IntrinsicAttributes
>(CompanyOverviewContent, "Link overview page");
